import React from 'react';
import { Alert, QueryErrorAlert } from '@shared/components';
import { formatMinorAmount } from '../../../utils/formatScheduleCurrency';
import { fixedDepositCeilingCopy, unpricedItemsCopy } from './copy';
import type { TermWarningCopy } from './copy';

/** Permanently mounted so the region exists in the a11y tree before content appears;
 *  self-collapses (renders no visible content) when there is nothing to warn about. */
export const TermWarningNotice: React.FC<{ termWarning: TermWarningCopy | null }> = ({ termWarning }) => (
    <div data-testid="schedule-term-underivable-region" role="status">
        {termWarning && (
            <div data-testid="schedule-term-underivable">
                <Alert variant="warning" role="note">
                    <div>
                        <h2 className="font-medium">{termWarning.heading}</h2>
                        <p className="mt-1">
                            {`${termWarning.cause} ${termWarning.consequence} ${termWarning.remedy}`}
                        </p>
                    </div>
                </Alert>
            </div>
        )}
    </div>
);

export const LeadRejectionNotice: React.FC<{ message: string | null }> = ({ message }) => (
    <div data-testid="collection-lead-out-of-bounds-region" role="status">
        {message !== null && (
            <div data-testid="collection-lead-out-of-bounds">
                <Alert variant="error" role="note">
                    {message}
                </Alert>
            </div>
        )}
    </div>
);

export const PastServiceDateNotice: React.FC<{ show: boolean }> = ({ show }) =>
    !show ? null : (
        <div data-testid="past-service-date">
            <Alert variant="error">
                A service on this quote is scheduled in the past, so a valid payment schedule
                cannot be built. Go back to the Configure step and update the service dates.
            </Alert>
        </div>
    );

export const DepositCeilingNotice: React.FC<{
    rejectionMinor: number | undefined;
    configuredMaxBinds: boolean;
    configuredMaxDepositMinor: number | undefined;
    currency: string;
    fixedAmountMinor?: number;
    naturalCeilingLabel?: string;
}> = ({
    rejectionMinor,
    configuredMaxBinds,
    configuredMaxDepositMinor,
    currency,
    fixedAmountMinor,
    naturalCeilingLabel,
}) => {
    if (rejectionMinor === undefined) {
        return null;
    }
    if (fixedAmountMinor !== undefined) {
        return (
            <div data-testid="deposit-exceeds-first-target">
                <Alert variant="error">
                    {fixedDepositCeilingCopy(
                        fixedAmountMinor,
                        rejectionMinor,
                        naturalCeilingLabel ?? 'the plan total including VAT',
                        currency,
                    )}
                </Alert>
            </div>
        );
    }
    return (
        <div data-testid="deposit-exceeds-first-target">
            <Alert variant="error">
                <p>
                    {configuredMaxBinds && configuredMaxDepositMinor !== undefined ? (
                        <>
                            Reduce the deposit to the maximum deposit for this product (
                            {formatMinorAmount(configuredMaxDepositMinor, currency)}) or less to preview
                            the schedule.
                        </>
                    ) : (
                        <>
                            Reduce the deposit below the first collection target (
                            {formatMinorAmount(rejectionMinor, currency)}) to preview the schedule.
                        </>
                    )}
                </p>
            </Alert>
        </div>
    );
};

export const UnpricedNotice: React.FC<{ itemNames: readonly string[] | null }> = ({ itemNames }) => {
    if (itemNames === null) {
        return null;
    }
    return (
        <div data-testid="basket-gross-unpriced">
            <Alert variant="error">{unpricedItemsCopy(itemNames)}</Alert>
        </div>
    );
};

export const PricingPendingNotice: React.FC<{ pending: boolean }> = ({ pending }) => (
    <div data-testid="basket-pricing-pending" role="status">
        {pending && (
            <Alert variant="info" role="note">
                Updating the basket price…
            </Alert>
        )}
    </div>
);

export const PricingErrorNotice: React.FC<{ hasError: boolean; onRetry: () => void }> = ({
    hasError,
    onRetry,
}) => {
    if (!hasError) {
        return null;
    }
    return (
        <div data-testid="basket-pricing-error">
            <QueryErrorAlert message="Unable to price this basket." onRetry={onRetry} />
        </div>
    );
};
